/**
 * The support queue.
 *
 * The SLA columns are the reason this register is not just "tickets": a queue
 * that cannot show what is late is a list. `response_overdue` and
 * `resolution_overdue` are computed by the server against the clock that stops
 * while a ticket waits on the client, so they answer "is the *team* late"
 * rather than "how old is this".
 *
 * There is no client filter control here, and that is deliberate: the client
 * column filters like any other, and a separate picker would be a second way to
 * do the same thing with its own state to keep in step.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Building2, Plus, Ticket, User } from 'lucide-react';
import {
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  Tooltip,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDateTime,
  formatRelative,
  type ServerColumn,
} from '@connect/web-shared';

import { PRIORITY_TONES, TICKET_STATUS_LABELS, TICKET_STATUS_TONES } from '../../lib/tones';
import { RaiseTicketDialog } from './raise-ticket-dialog';

export interface TicketRow {
  public_id: string;
  code: string;
  subject: string;
  status: string;
  priority: string;
  client_public_id: string;
  client_name: string;
  project_name: string | null;
  assignee_name: string | null;
  raised_by: string | null;
  comment_count: number;
  response_overdue: boolean;
  resolution_overdue: boolean;
  first_response_at: string | null;
  resolved_at: string | null;
  created_at: string;
}

interface SupportOptions {
  statuses: { value: string; label: string }[];
  priorities: { value: string; label: string }[];
}

const ENDPOINT = '/support/tickets';

export function TicketsPage() {
  const navigate = useNavigate();
  const [raising, setRaising] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const options = useQuery({
    queryKey: ['support', 'options'],
    queryFn: () => api.get<SupportOptions>('/support/options'),
    staleTime: 10 * 60_000,
  });

  const fetchRows = useMemo(() => createServerFetcher<TicketRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<TicketRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Reference',
        filter: 'text',
        width: 120,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'subject',
        header: 'Ticket',
        icon: <Ticket className="size-3" />,
        filter: 'text',
        minWidth: 260,
        render: (row) => (
          <span className="flex items-center gap-1.5">
            <span className="truncate font-medium">{row.subject}</span>
            {/* One badge for both clocks, because the remedy is the same:
                somebody has to pick it up now. Which clock it is goes in the
                tooltip rather than a second column nobody reads. */}
            {row.response_overdue || row.resolution_overdue ? (
              <Tooltip
                content={
                  row.response_overdue
                    ? 'Nobody has replied to the client yet, and the response target has passed.'
                    : 'Still open past its resolution target.'
                }
              >
                <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-danger-subtle px-1.5 py-0.5 text-2xs font-medium text-danger">
                  <AlarmClock className="size-3" />
                  Late
                </span>
              </Tooltip>
            ) : null}
          </span>
        ),
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 170,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: options.data?.statuses,
        width: 150,
        render: (row) => (
          <StatusChip
            value={row.status}
            tones={TICKET_STATUS_TONES}
            labels={TICKET_STATUS_LABELS}
          />
        ),
      },
      {
        field: 'priority',
        header: 'Priority',
        filter: 'select',
        options: options.data?.priorities,
        width: 110,
        render: (row) => <StatusChip value={row.priority} tones={PRIORITY_TONES} />,
      },
      {
        field: 'assignee_name',
        header: 'Assignee',
        icon: <User className="size-3" />,
        filter: 'select',
        minWidth: 150,
        render: (row) =>
          row.assignee_name ? (
            <span className="truncate">{row.assignee_name}</span>
          ) : (
            // Unassigned is a state worth seeing, not an empty cell.
            <span className="text-warning">Unassigned</span>
          ),
      },
      { field: 'project_name', header: 'Project', filter: 'select', width: 170, hiddenByDefault: true },
      { field: 'raised_by', header: 'Raised by', filter: 'select', width: 160, hiddenByDefault: true },
      {
        field: 'comment_count',
        header: 'Replies',
        numeric: true,
        width: 90,
        sortable: false,
      },
      {
        field: 'created_at',
        header: 'Raised',
        filter: 'date',
        width: 140,
        render: (row) => (
          <span title={formatDateTime(row.created_at)}>{formatRelative(row.created_at)}</span>
        ),
      },
      {
        field: 'resolved_at',
        header: 'Resolved',
        filter: 'date',
        width: 140,
        hiddenByDefault: true,
        format: (value) => (value ? formatDateTime(value as string) : '—'),
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Tickets"
        description="Everything clients have raised, and everything raised on their behalf."
        actions={
          <Can permission="support.ticket.create">
            <Button size="sm" onClick={() => setRaising(true)}>
              <Plus className="size-3.5" />
              New ticket
            </Button>
          </Can>
        }
      />

      <ServerTable<TicketRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['support', 'tickets']}
        stateKey="support.tickets"
        searchPlaceholder="Search by subject, code or client"
        viewField="subject"
        onView={(row) => navigate(`/tickets/${row.public_id}`)}
        recentKey={recentKey}
        rowDetailFields={(row) => [
          { label: 'Client', value: row.client_name },
          { label: 'Project', value: row.project_name },
          { label: 'Assignee', value: row.assignee_name },
          { label: 'Raised by', value: row.raised_by },
          {
            label: 'First reply',
            value: row.first_response_at ? formatDateTime(row.first_response_at) : 'Not yet',
          },
          {
            label: 'Resolved',
            value: row.resolved_at ? formatDateTime(row.resolved_at) : 'Not yet',
          },
        ]}
        emptyTitle="Nothing in the queue"
        emptyDescription="Tickets raised by clients in their portal land here, and staff can raise one on a client's behalf."
        emptyAction={
          <Can permission="support.ticket.create">
            <Button size="sm" onClick={() => setRaising(true)}>
              <Plus className="size-3.5" />
              Raise a ticket
            </Button>
          </Can>
        }
      />

      <RaiseTicketDialog
        open={raising}
        onClose={() => setRaising(false)}
        onRaised={(publicId) => {
          setRecentKey(publicId);
          setRaising(false);
        }}
      />
    </div>
  );
}
